import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import FormBuildTab from '../components/forms/FormBuildTab';
import FormSettingsTab from '../components/forms/FormSettingsTab';
import FormNotificationsTab from '../components/forms/FormNotificationsTab';
import FormAfterSubmitTab from '../components/forms/FormAfterSubmitTab';
import FormSubmissionsTab from '../components/forms/FormSubmissionsTab';
const TABS = [
    { id: 'build', label: 'Build', icon: '✎', hint: 'Form fields' },
    { id: 'settings', label: 'Settings', icon: '⚙', hint: 'Name, labels, messages' },
    { id: 'notifications', label: 'Notifications', icon: '📧', hint: 'Webhook + email recipients' },
    { id: 'after-submit', label: 'After submit', icon: '➜', hint: 'What happens on success' },
    { id: 'submissions', label: 'Submissions', icon: '📥', hint: 'Received entries' },
];
export default function FormEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const tab = (() => {
        const t = params.get('tab');
        return TABS.some((x) => x.id === t) ? t : 'build';
    })();
    const setTab = (next) => {
        const p = new URLSearchParams(params);
        p.set('tab', next);
        setParams(p, { replace: true });
    };
    // saved + draft state — same model the page editor uses.
    const [saved, setSaved] = useState(null);
    const [draft, setDraft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busySave, setBusySave] = useState(false);
    const [smtpReady, setSmtpReady] = useState(true);
    const dirty = useMemo(() => {
        if (!saved || !draft)
            return false;
        return JSON.stringify(saved) !== JSON.stringify(draft);
    }, [saved, draft]);
    const load = useCallback(async () => {
        if (!id)
            return;
        setLoading(true);
        setError(null);
        try {
            const [{ form }, list] = await Promise.all([
                api.getForm(id),
                api.listForms().catch(() => ({ forms: [], smtp_configured: true })),
            ]);
            setSaved(form);
            setDraft(form);
            setSmtpReady(!!list.smtp_configured);
        }
        catch (e) {
            setError(e?.message ?? 'Failed to load form');
        }
        finally {
            setLoading(false);
        }
    }, [id]);
    useEffect(() => { load(); }, [load]);
    // beforeunload guard for unsaved drafts.
    useEffect(() => {
        function onBefore(e) {
            if (!dirty)
                return;
            e.preventDefault();
            e.returnValue = '';
        }
        window.addEventListener('beforeunload', onBefore);
        return () => window.removeEventListener('beforeunload', onBefore);
    }, [dirty]);
    async function handleSave() {
        if (!id || !draft)
            return;
        setBusySave(true);
        setError(null);
        try {
            const r = await api.updateForm(id, draft);
            setSaved(r.form);
            setDraft(r.form);
        }
        catch (e) {
            setError(e?.message ?? 'Save failed');
        }
        finally {
            setBusySave(false);
        }
    }
    function handleDiscard() {
        if (!saved)
            return;
        if (dirty && !window.confirm('Discard unsaved changes?'))
            return;
        setDraft(saved);
        setError(null);
    }
    async function handleDelete() {
        if (!id)
            return;
        if (!window.confirm(`Delete form "${id}"? Existing submissions are kept on disk.`))
            return;
        try {
            await api.deleteForm(id);
            navigate('/forms', { replace: true });
        }
        catch (e) {
            alert(e?.message ?? 'Delete failed');
        }
    }
    // Ctrl/Cmd+S
    useEffect(() => {
        function onKey(e) {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                if (dirty)
                    handleSave();
            }
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });
    if (loading)
        return _jsx("div", { className: "page-shell", children: _jsx("div", { className: "muted pad", children: "Loading\u2026" }) });
    if (error && !draft)
        return _jsx("div", { className: "page-shell", children: _jsx("div", { className: "err pad", children: error }) });
    if (!draft)
        return null;
    return (_jsxs("div", { className: "page-shell form-editor", children: [_jsxs("div", { className: "form-editor-sticky", children: [_jsxs("div", { className: "page-header form-editor-header", children: [_jsxs("div", { children: [_jsx("div", { className: "muted small", children: _jsx(Link, { to: "/forms", children: "\u2190 Forms" }) }), _jsxs("h1", { children: [draft.name || draft.id, dirty && _jsx("span", { className: "tag warn-tag", style: { marginLeft: 10, fontSize: '0.7em' }, children: "\u25CF Unsaved" })] }), _jsxs("p", { className: "muted small", style: { marginTop: 2 }, children: ["Embed with ", _jsx("code", { children: `{{form:${draft.id}}}` }), " on any page."] })] }), _jsxs("div", { className: "actions", children: [_jsx("button", { className: "ghost danger", onClick: handleDelete, disabled: busySave, children: "Delete" }), _jsx("button", { className: "ghost", onClick: handleDiscard, disabled: !dirty || busySave, children: "Discard" }), _jsx("button", { onClick: handleSave, disabled: !dirty || busySave, children: busySave ? 'Saving…' : 'Save' })] })] }), error && _jsx("div", { className: "err small pad-y", children: error }), _jsx("nav", { className: "form-tabs", role: "tablist", children: TABS.map((t) => (_jsxs("button", { role: "tab", "aria-selected": tab === t.id, className: tab === t.id ? 'form-tab active' : 'form-tab', onClick: () => setTab(t.id), title: t.hint, children: [_jsx("span", { className: "form-tab-icon", children: t.icon }), _jsx("span", { className: "form-tab-label", children: t.label })] }, t.id))) })] }), _jsxs("div", { className: "form-tab-body", children: [tab === 'build' && _jsx(FormBuildTab, { draft: draft, setDraft: setDraft }), tab === 'settings' && _jsx(FormSettingsTab, { draft: draft, setDraft: setDraft }), tab === 'notifications' && (_jsx(FormNotificationsTab, { draft: draft, setDraft: setDraft, smtpReady: smtpReady, formId: draft.id })), tab === 'after-submit' && _jsx(FormAfterSubmitTab, { draft: draft, setDraft: setDraft }), tab === 'submissions' && _jsx(FormSubmissionsTab, { formId: draft.id, formDef: saved ?? draft, smtpReady: smtpReady })] })] }));
}
